import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import debounce from 'lodash/debounce';
import type { RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

export const SELECT_DATE_DEBOUNCE: Milliseconds = 300;

interface UseSelectByScrollProps {
  readonly onSelect: (index: number) => void;
  readonly itemHeight: number;
  readonly shouldSelect?: boolean;
}

interface UseSelectByScrollReturn {
  readonly ref: RefObject<HTMLDivElement>;
}

export const useSelectByScroll = ({
  itemHeight,
  onSelect,
  shouldSelect = true,
}: UseSelectByScrollProps): UseSelectByScrollReturn => {
  const ref = useRef<HTMLDivElement>(null);

  const select = useCallback(() => {
    /* istanbul ignore if -- @preserve - can't mock null ref */
    if (ref.current == null || !shouldSelect) {
      return;
    }

    const index = Math.round(ref.current.scrollTop / itemHeight);
    onSelect(index);
  }, [itemHeight, onSelect, shouldSelect]);

  const debouncedSelect = useMemo(() => debounce(select, SELECT_DATE_DEBOUNCE), [select]);

  useEffect(() => {
    const wrapper = ref.current ?? null;

    if (wrapper) {
      wrapper.addEventListener('scroll', debouncedSelect);
    }

    return () => {
      if (wrapper) {
        wrapper.removeEventListener('scroll', debouncedSelect);
      }
    };
  }, [debouncedSelect]);

  return { ref };
};
